import React, { useState } from 'react';
import { Modal, Button, Table, Badge, Card, Row, Col, Spinner, Form } from 'react-bootstrap';
import { FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, ArrowUpRight, Download, Filter } from 'lucide-react';
import { downloadBulkImportTemplate } from '../utils/excelTemplateGenerator';

const BulkImportPreviewModal = ({ show, onHide, fileName, stats, onConfirm, loading }) => {
  const [filterAction, setFilterAction] = useState('all');

  if (!stats) return null;

  const {
    total_rows = 0,
    to_create = 0,
    to_update_area_cc = 0,
    skipped_invalid = 0,
    preview_rows = [],
    missing_clients = []
  } = stats;

  const totalValidToProcess = to_create + to_update_area_cc;

  // Filtrar filas según selección
  const filteredRows = preview_rows.filter(r => {
    if (filterAction === 'all') return true;
    if (filterAction === 'create') return r.action === 'create';
    if (filterAction === 'update') return r.action === 'update_area_cc';
    if (filterAction === 'error') return r.action === 'error';
    if (filterAction === 'skip') return r.action?.startsWith('skip');
    return true;
  });

  const getActionBadge = (action) => {
    switch (action) {
      case 'create':
        return <Badge bg="success" className="px-2 py-1"><CheckCircle2 size={12} className="me-1" /> Nueva Reserva</Badge>;
      case 'update_area_cc':
        return <Badge bg="warning" text="dark" className="px-2 py-1"><ArrowUpRight size={12} className="me-1" /> Actualizar Datos</Badge>;
      case 'error':
        return <Badge bg="danger" className="px-2 py-1"><XCircle size={12} className="me-1" /> Inválida / Descartada</Badge>;
      default:
        return <Badge bg="secondary" className="px-2 py-1">Sin Cambios</Badge>;
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static" keyboard={false}>
      <Modal.Header closeButton className="bg-dark text-white py-2">
        <Modal.Title className="h6 mb-0 d-flex align-items-center gap-2">
          <FileSpreadsheet size={20} className="text-primary" />
          Vista Previa de Importación Masiva — {fileName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-light p-3" style={{ maxHeight: '78vh', overflowY: 'auto' }}>

        {/* --- TARJETAS DE MÉTRICAS DE RESUMEN --- */}
        <Row className="g-2 mb-3">
          <Col md={3}>
            <Card className="border-0 shadow-sm bg-white text-center py-2">
              <Card.Body className="p-2">
                <div className="text-muted small fw-bold">TOTAL FILAS</div>
                <div className="h3 mb-0 fw-bold text-dark">{total_rows}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm bg-success bg-opacity-10 border-start border-success border-4 text-center py-2">
              <Card.Body className="p-2">
                <div className="text-success small fw-bold">NUEVAS A CREAR</div>
                <div className="h3 mb-0 fw-bold text-success">+{to_create}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm bg-warning bg-opacity-10 border-start border-warning border-4 text-center py-2">
              <Card.Body className="p-2">
                <div className="text-warning-emphasis small fw-bold">A ACTUALIZAR</div>
                <div className="h3 mb-0 fw-bold text-warning-emphasis">+{to_update_area_cc}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm bg-danger bg-opacity-10 border-start border-danger border-4 text-center py-2">
              <Card.Body className="p-2">
                <div className="text-danger small fw-bold">DESCARTADAS / ERRORES</div>
                <div className="h3 mb-0 fw-bold text-danger">{skipped_invalid}</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* --- ALERTAS DE CLIENTES O ERRORES CRÍTICOS --- */}
        {missing_clients.length > 0 && (
          <Card className="border-danger bg-danger bg-opacity-10 mb-3 shadow-sm">
            <Card.Body className="p-2 text-danger small">
              <div className="fw-bold d-flex align-items-center gap-1">
                <AlertTriangle size={16} /> Clientes no encontrados en la Base de Datos:
              </div>
              <ul className="mb-0 mt-1 ps-3">
                {missing_clients.map((c, i) => (
                  <li key={i}><strong>{c}</strong> — Se omitirán sus filas hasta que registres la empresa en Clientes.</li>
                ))}
              </ul>
            </Card.Body>
          </Card>
        )}

        {/* --- FILTRADO DE TABLA --- */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center gap-2">
            <Filter size={16} className="text-muted" />
            <span className="small fw-bold text-secondary">Filtrar vista previa:</span>
            <Form.Select
              size="sm"
              style={{ width: '200px' }}
              value={filterAction}
              onChange={e => setFilterAction(e.target.value)}
            >
              <option value="all">Ver Todas ({preview_rows.length})</option>
              <option value="create">Solo Nuevas ({to_create})</option>
              <option value="update">Solo Actualizaciones ({to_update_area_cc})</option>
              <option value="error">Solo Inválidas ({skipped_invalid})</option>
            </Form.Select>
          </div>
          <span className="small text-muted">Mostrando {filteredRows.length} de {preview_rows.length} registros</span>
        </div>

        {/* --- TABLA DE VISTA PREVIA --- */}
        <Card className="shadow-sm border-0">
          <Table hover responsive size="sm" className="mb-0 text-nowrap align-middle" style={{ fontSize: '13px' }}>
            <thead className="bg-light text-secondary">
              <tr>
                <th className="py-2">Ref / OS</th>
                <th className="py-2">Pasajero</th>
                <th className="py-2">Fecha / Hora</th>
                <th className="py-2">Acción</th>
                <th className="py-2">Detalle de Importación</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    No hay registros para mostrar con el filtro seleccionado.
                  </td>
                </tr>
              ) : (
                filteredRows.map((r, idx) => (
                  <tr key={idx} className={r.action === 'error' ? 'table-danger' : r.action === 'create' ? 'table-success bg-opacity-10' : ''}>
                    <td className="fw-bold">{r.ref}</td>
                    <td>{r.passenger}</td>
                    <td>{r.fecha}</td>
                    <td>{getActionBadge(r.action)}</td>
                    <td className="small">{r.detail}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card>
      </Modal.Body>

      <Modal.Footer className="bg-light d-flex justify-content-between py-2">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={downloadBulkImportTemplate}
          className="d-flex align-items-center gap-1"
        >
          <Download size={14} /> Descargar Plantilla Excel
        </Button>
        <div className="d-flex gap-2">
          <Button variant="secondary" size="sm" onClick={onHide} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="success"
            size="sm"
            className="fw-bold px-3 d-flex align-items-center gap-2"
            onClick={onConfirm}
            disabled={loading || totalValidToProcess === 0}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" />
                Procesando Reserva...
              </>
            ) : (
              <>
                <CheckCircle2 size={16} />
                Confirmar e Importar {totalValidToProcess} Reservas
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkImportPreviewModal;
